import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * useRealtime — drop-in replacement for `useEffect(() => api.get(path)…, [])` patterns.
 *
 * Refreshes the data automatically when:
 *   • the component mounts
 *   • the browser/app returns to the foreground (`visibilitychange`)
 *   • the window regains focus
 *   • on a fixed interval (default 20 s) while the tab is visible
 *
 * The polling interval pauses when the page is hidden, so it doesn't burn CPU
 * or battery on a phone tucked in a pocket. It resumes the moment the user opens
 * the app again — which is exactly the moment a Capacitor-wrapped SC app needs
 * to "feel real-time".
 *
 * Usage:
 *   const { data, loading, refresh } = useRealtime("/sc/dashboard");
 *   if (!data) return <Spinner />;
 *   …
 */
export default function useRealtime(path, { interval = 20000, deps = [] } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Keep a stable ref to the current path so the visibility/focus listeners
  // never close over a stale value.
  const pathRef = useRef(path);
  pathRef.current = path;

  const refresh = useCallback(async () => {
    if (!pathRef.current) return;
    try {
      const r = await api.get(pathRef.current);
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(err);
      // Don't blow away previously-fetched data on a transient error;
      // the user keeps seeing the last successful snapshot.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial fetch + reset when path/deps change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); refresh(); }, [path, ...deps]);

  // Refresh on visibility/focus + interval poll
  useEffect(() => {
    let timer = null;

    function startPoll() {
      stopPoll();
      timer = setInterval(refresh, interval);
    }
    function stopPoll() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function onVisibility() {
      if (document.visibilityState === "visible") {
        refresh();
        startPoll();
      } else {
        stopPoll();
      }
    }
    function onFocus() {
      refresh();
    }

    // Initial state: if the page is already visible, start polling
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      startPoll();
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    return () => {
      stopPoll();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [interval, refresh]);

  return { data, loading, error, refresh };
}
